"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import type { ArenaPlacement, ArenaPlayerStat } from "@/shared/api/arena";
import { CHAMPION_PLACEHOLDER_URL, getItemImageUrl } from "@/shared/lib/champion-image";
import { cn } from "@/shared/lib/utils";

import { getChampionSquareById, useArenaAugments } from "./arena-assets";

const PLACE_LABEL = ["", "1ST", "2ND", "3RD", "4TH", "5TH", "6TH"];

/** Подменяет битую картинку на заглушку, не ломая ряд. */
function onImgError(e: React.SyntheticEvent<HTMLImageElement>) {
  const el = e.currentTarget;
  if (el.dataset["fallback"]) return;
  el.dataset["fallback"] = "1";
  el.src = CHAMPION_PLACEHOLDER_URL;
}

/** Очки за место: из awarded-результата (если есть), иначе проекция по таблице очков. */
function pointsForGroup(
  group: ArenaPlayerStat[],
  placements?: ArenaPlacement[],
  scoring?: Record<string, number>,
): number | null {
  const ttid = group.find((p) => p.tournamentTeamId)?.tournamentTeamId ?? null;
  if (ttid && placements?.length) {
    const pl = placements.find((p) => p.tournamentTeamId === ttid);
    if (pl) return pl.pointsAwarded;
  }
  const place = group[0]?.subteamPlacement;
  if (scoring && place != null) {
    const v = scoring[String(place)] ?? scoring[place as unknown as string];
    if (typeof v === "number") return v;
  }
  return null;
}

function kda(p: ArenaPlayerStat): string {
  const ratio = p.deaths === 0 ? p.kills + p.assists : (p.kills + p.assists) / p.deaths;
  return ratio.toFixed(1);
}

function fmt(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/** Группировка игроков по субкомандам, отсортированная по месту. */
function groupBySubteam(players: ArenaPlayerStat[]) {
  const groups = new Map<number, ArenaPlayerStat[]>();
  for (const p of players) {
    const arr = groups.get(p.playerSubteamId) ?? [];
    arr.push(p);
    groups.set(p.playerSubteamId, arr);
  }
  return [...groups.values()].sort(
    (a, b) => (a[0]?.subteamPlacement ?? 99) - (b[0]?.subteamPlacement ?? 99),
  );
}

export function ArenaGameScoreboard({
  players,
  placements,
  scoring,
}: {
  players: ArenaPlayerStat[];
  placements?: ArenaPlacement[];
  scoring?: Record<string, number>;
}) {
  const tUi = useTranslations("Ui");
  const [tab, setTab] = useState<"overview" | "stats">("overview");
  if (!players || players.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex gap-1 text-xs">
        <button
          type="button"
          onClick={() => setTab("overview")}
          className={cn(
            "rounded-md px-3 py-1 font-medium",
            tab === "overview" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          {tUi("arenaGameScoreboard.obzor")}
        </button>
        <button
          type="button"
          onClick={() => setTab("stats")}
          className={cn(
            "rounded-md px-3 py-1 font-medium",
            tab === "stats" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          Stats
        </button>
      </div>
      {tab === "overview" ? (
        <Overview players={players} placements={placements} scoring={scoring} />
      ) : (
        <StatsTable players={players} />
      )}
    </div>
  );
}

// ── Обзор (команды → игроки) ─────────────────────────────────────────────────

function Overview({
  players,
  placements,
  scoring,
}: {
  players: ArenaPlayerStat[];
  placements?: ArenaPlacement[];
  scoring?: Record<string, number>;
}) {
  const tUi = useTranslations("Ui");
  const augMap = useArenaAugments();
  const groups = useMemo(() => groupBySubteam(players), [players]);
  const maxDmg = Math.max(1, ...players.map((p) => p.totalDamageDealtToChampions));

  return (
    <div className="space-y-3">
      {groups.map((team, i) => {
        const place = team[0]?.subteamPlacement ?? i + 1;
        const name = team[0]?.teamName || tUi("litArenaGameScoreboard.komanda", { n: team[0]?.playerSubteamId ?? i + 1 });
        const teamKills = team.reduce((s, p) => s + p.kills, 0);
        const teamDeaths = team.reduce((s, p) => s + p.deaths, 0);
        const teamAssists = team.reduce((s, p) => s + p.assists, 0);
        const points = pointsForGroup(team, placements, scoring);
        return (
          <div key={i} className="overflow-hidden rounded-xl border">
            <div
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 text-xs font-semibold",
                place === 1 ? "bg-yellow-500/15 text-yellow-500" : "bg-muted/40",
              )}
            >
              <span>{PLACE_LABEL[place] ?? place}</span>
              <span className="truncate text-foreground">{name}</span>
              {points != null && (
                <span className="rounded bg-primary/15 px-1.5 py-0.5 font-mono text-primary">
                  +{points}
                </span>
              )}
              <span className="ml-auto font-mono text-muted-foreground">
                {teamKills}/{teamDeaths}/{teamAssists}
              </span>
            </div>
            <div className="divide-y">
              {team.map((p) => (
                <PlayerRow key={p.puuid} p={p} maxDmg={maxDmg} augMap={augMap} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PlayerRow({
  p,
  maxDmg,
  augMap,
}: {
  p: ArenaPlayerStat;
  maxDmg: number;
  augMap: Record<number, { icon: string; name: string }>;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5">
      {/* champ */}
      <div className="relative shrink-0">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError — фолбэк загрузки картинки, не пользовательское действие */}
        <img src={getChampionSquareById(p.championId)} onError={onImgError} alt="" className="h-9 w-9 rounded" />
        <span className="absolute -bottom-1 -right-1 rounded bg-black/80 px-1 text-[10px] font-bold text-white">
          {p.champLevel}
        </span>
      </div>
      {/* augments */}
      <div className="flex shrink-0 gap-0.5">
        {p.augments.filter((a) => a > 0).slice(0, 6).map((a, i) =>
          augMap[a] ? (
            <img key={i} src={augMap[a].icon} alt={augMap[a].name} title={augMap[a].name} className="h-5 w-5 rounded-sm" />
          ) : (
            <span key={i} className="h-5 w-5 rounded-sm bg-muted" />
          ),
        )}
      </div>
      {/* items */}
      <div className="hidden shrink-0 gap-0.5 sm:flex">
        {p.items.slice(0, 7).map((it, i) =>
          it > 0 ? (
            <img key={i} src={getItemImageUrl(it)} alt="" className="h-5 w-5 rounded-sm" />
          ) : (
            <span key={i} className="h-5 w-5 rounded-sm bg-muted/40" />
          ),
        )}
      </div>
      {/* name */}
      <div className="min-w-0 flex-1 truncate text-sm">
        {p.riotIdGameName || "—"}
      </div>
      {/* kda */}
      <div className="shrink-0 text-right text-xs">
        <div className="font-mono">
          {p.kills}/{p.deaths}/{p.assists}
        </div>
        <div className="text-muted-foreground">{kda(p)} KDA</div>
      </div>
      {/* dmg */}
      <div className="hidden w-24 shrink-0 md:block">
        <div className="text-right text-xs font-mono">{fmt(p.totalDamageDealtToChampions)}</div>
        <div className="mt-0.5 h-1.5 rounded bg-muted">
          <div
            className="h-full rounded bg-pink-500"
            style={{ width: `${(p.totalDamageDealtToChampions / maxDmg) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Stats (сравнительная таблица) ────────────────────────────────────────────

function StatsTable({ players }: { players: ArenaPlayerStat[] }) {
  const tUi = useTranslations("Ui");
  const ordered = useMemo(
    () => [...players].sort((a, b) => a.subteamPlacement - b.subteamPlacement),
    [players],
  );

  // raw — числовое значение для сравнения (подсветка максимума в строке).
  const rows: { label: string; get: (p: ArenaPlayerStat) => string; raw: (p: ArenaPlayerStat) => number }[] = [
    { label: "KDA", get: (p) => `${p.kills}/${p.deaths}/${p.assists}`, raw: (p) => (p.deaths === 0 ? p.kills + p.assists : (p.kills + p.assists) / p.deaths) },
    { label: "Killing Spree", get: (p) => String(p.largestKillingSpree), raw: (p) => p.largestKillingSpree },
    { label: "Multi Kill", get: (p) => String(p.largestMultiKill), raw: (p) => p.largestMultiKill },
    { label: "CC Score", get: (p) => String(p.timeCCingOthers), raw: (p) => p.timeCCingOthers },
    { label: "Dmg → Champs", get: (p) => fmt(p.totalDamageDealtToChampions), raw: (p) => p.totalDamageDealtToChampions },
    { label: "Physical", get: (p) => fmt(p.physicalDamageDealtToChampions), raw: (p) => p.physicalDamageDealtToChampions },
    { label: "Magic", get: (p) => fmt(p.magicDamageDealtToChampions), raw: (p) => p.magicDamageDealtToChampions },
    { label: "True", get: (p) => fmt(p.trueDamageDealtToChampions), raw: (p) => p.trueDamageDealtToChampions },
    { label: "Largest Crit", get: (p) => fmt(p.largestCriticalStrike), raw: (p) => p.largestCriticalStrike },
    { label: "Dmg Taken", get: (p) => fmt(p.totalDamageTaken), raw: (p) => p.totalDamageTaken },
    { label: "Self Mitigated", get: (p) => fmt(p.damageSelfMitigated), raw: (p) => p.damageSelfMitigated },
    { label: "Healed", get: (p) => fmt(p.totalHeal), raw: (p) => p.totalHeal },
    { label: "Gold Earned", get: (p) => fmt(p.goldEarned), raw: (p) => p.goldEarned },
    { label: "Gold Spent", get: (p) => fmt(p.goldSpent), raw: (p) => p.goldSpent },
    { label: "Minions", get: (p) => String(p.totalMinionsKilled), raw: (p) => p.totalMinionsKilled },
  ];

  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b bg-muted/40">
            <th className="sticky left-0 z-10 bg-muted/40 px-2 py-2 text-left font-medium">{tUi("arenaGameScoreboard.metrika")}</th>
            {ordered.map((p) => (
              <th key={p.puuid} className="px-2 py-2 align-bottom text-center font-medium" aria-label={p.riotIdGameName || "—"}>
                <div className="flex flex-col items-center gap-1">
                  {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError — фолбэк загрузки картинки, не пользовательское действие */}
                  <img
                    src={getChampionSquareById(p.championId)}
                    onError={onImgError}
                    alt=""
                    title={p.riotIdGameName ?? ""}
                    className="h-7 w-7 rounded"
                  />
                  <span
                    className="max-w-[68px] truncate text-[10px] font-normal text-muted-foreground"
                    title={p.riotIdGameName ?? ""}
                  >
                    {p.riotIdGameName || "—"}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const max = Math.max(...ordered.map(r.raw));
            return (
              <tr key={r.label} className="border-b last:border-0">
                <td className="sticky left-0 z-10 bg-background px-2 py-1.5 text-muted-foreground">{r.label}</td>
                {ordered.map((p) => {
                  const isMax = max > 0 && r.raw(p) === max;
                  return (
                    <td
                      key={p.puuid}
                      className={cn(
                        "px-2 py-1.5 text-center font-mono",
                        isMax && "bg-primary/15 font-bold text-primary",
                      )}
                    >
                      {r.get(p)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
